import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '@app/core/auth/stores/auth-store';
import { ProjectsService } from '@app/core/services/projects.service';
import { SessionInitService } from '@app/core/auth/services/session-init.service';
import { devLog, devWarn } from '@app/core/utils/dev-logger';

/**
 * Login Redirect Guard
 *
 * Prevents authenticated users from accessing login/root pages by redirecting them
 * to their default project dashboard.
 *
 * Use on: root path ('/') and '/login' route
 *
 * Behavior:
 * - Authenticated user → Wait for initialization, then redirect to project dashboard
 * - Locked session → Redirect to /locked page
 * - Unauthenticated → Allow access to login
 *
 * Important: During page reload, APP_INITIALIZER loads projects before routing.
 * If projects aren't loaded yet, we're still in initialization phase - allow access
 * to prevent infinite loop, the Login component will handle redirection after init.
 *
 * Note: All injections are done inside the guard function (not at module level)
 * to avoid circular dependency issues during app initialization.
 */
export const loginRedirectGuard: CanActivateFn = (route, state) => {
  // Lazy inject all dependencies to avoid circular dependency during initialization
  let authStore: AuthStore;
  let projectsService: ProjectsService;
  let sessionInit: SessionInitService;
  let router: Router;

  try {
    authStore = inject(AuthStore);
    projectsService = inject(ProjectsService);
    sessionInit = inject(SessionInitService);
    router = inject(Router);
  } catch (error) {
    // If injection fails during initialization, allow access
    // The login page will handle proper redirection once app is ready
    devWarn('⚠️ LoginRedirectGuard: Dependencies not ready yet, allowing access');
    return true;
  }

  const authState = authStore.authState();

  // If session is locked, redirect to locked page
  if (authState === 'locked') {
    return router.createUrlTree(['/locked']);
  }

  // If user is authenticated, redirect to their default project
  if (authState === 'authenticated') {
    const activeProject = projectsService.activeProject();
    const isInitializing = sessionInit.isInitializing();

    if (activeProject) {
      devLog(`🔄 LoginRedirectGuard: User authenticated, redirecting to project ${activeProject.id}`);
      return router.createUrlTree([`/project/${activeProject.id}/dashboard`]);
    }

    // If initializing (APP_INITIALIZER still running), allow access temporarily
    // This prevents infinite loop during page reload
    // Login component will show splash screen and redirect after initialization
    if (isInitializing) {
      devLog('⏳ LoginRedirectGuard: Still initializing, allowing temporary access');
      return true;
    }

    // If not initializing but still no projects, user might have zero projects
    // Allow access to login page which will show appropriate message
    devWarn('⚠️ LoginRedirectGuard: No projects available for authenticated user');
    return true;
  }

  // User is not authenticated - allow access to login
  return true;
};
